import { useEffect, useState, useCallback } from "react";
import { Loader2, Receipt, FileText, CalendarClock, TrendingUp, Users, Clock, Plus, X, Send } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";
import { StatusBadge, money, fmtDate } from "./pro/proUi";

const TABS = [
  { id: "orders", label: "Commandes", icon: Receipt },
  { id: "quotes", label: "Devis", icon: FileText },
  { id: "bookings", label: "Réservations", icon: CalendarClock },
];

// Espace Commercial Manager — LECTURE SEULE (aucune action commerciale).
export default function ManagerCommerce() {
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("orders");
  const [artist, setArtist] = useState("");
  const [orders, setOrders] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [showQuote, setShowQuote] = useState(false);

  const load = useCallback(() => {
    const p = artist ? `?artist_id=${artist}` : "";
    setOrders(null); setQuotes(null); setBookings(null);
    api.get(`/manager/commerce/orders${p}`).then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    api.get(`/manager/commerce/quotes${p}`).then((r) => setQuotes(r.data.quotes)).catch(() => setQuotes([]));
    api.get(`/manager/commerce/bookings${p}`).then((r) => setBookings(r.data.bookings)).catch(() => setBookings([]));
  }, [artist]);

  useEffect(() => { api.get("/manager/commerce/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  // Options artistes dérivées des données (aucun id fourni au backend sans revalidation ReBAC).
  const artistOptions = [];
  const seen = new Set();
  [...(orders || []), ...(quotes || []), ...(bookings || [])].forEach((x) => {
    if (x.artist_id && !seen.has(x.artist_id)) { seen.add(x.artist_id); artistOptions.push({ id: x.artist_id, name: x.artist_name }); }
  });

  return (
    <div className="space-y-6" data-testid="manager-commerce-page">
      <div>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">Commercial</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Suivi commercial</h1>
        <p className="text-zinc-500 mt-2 text-sm">Consulte et gère les devis de tes artistes. Le bénéficiaire du paiement reste l'artiste.</p>
        <button onClick={() => setShowQuote(true)} data-testid="new-quote-btn" className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-xl gold-gradient text-black font-semibold text-sm">
          <Plus className="w-4 h-4" />Émettre un devis (au nom d'un artiste)
        </button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="commerce-overview">
          <Kpi icon={Users} label="Artistes suivis" value={overview.managed_count} testid="kpi-managed" />
          <Kpi icon={FileText} label="Devis en attente" value={overview.quotes_pending} testid="kpi-quotes" />
          <Kpi icon={Receipt} label="Commandes actives" value={overview.orders_active} testid="kpi-orders" />
          <Kpi icon={Clock} label="En attente de paiement" value={overview.pending_payment} testid="kpi-pending" />
          <Kpi icon={TrendingUp} label="CA géré (encaissé)" value={money(overview.revenue_managed, overview.currency)} testid="kpi-revenue" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`commerce-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm border ${tab === t.id ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
        {artistOptions.length > 0 && (
          <select value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="commerce-artist-filter"
            className="ml-auto h-9 px-3 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-200 outline-none focus:border-amber-500">
            <option value="">Tous mes artistes</option>
            {artistOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>

      {tab === "orders" && <List data={orders} empty="Aucune commande pour tes artistes." testid="commerce-orders" render={(o) => (
        <Row key={o.id} title={o.title || o.request_title} sub={`${o.artist_name} · Client ${o.client_name || "—"}`} status={o.status}
          right={money(o.amount, o.currency)} meta={`Part artiste ${money(o.pro_amount, o.currency)} · ${fmtDate(o.created_at)}`}
          extra={o.dispute_status ? "Litige en cours" : (o.invoice_number ? `Facture ${o.invoice_number}` : null)} />
      )} />}

      {tab === "quotes" && <List data={quotes} empty="Aucun devis pour tes artistes." testid="commerce-quotes" render={(q) => (
        <Row key={q.id} title={q.title} sub={`${q.artist_name} · Client ${q.client_name || "—"}`} status={q.status}
          right={money(q.amount, q.currency)} meta={fmtDate(q.created_at)} />
      )} />}

      {tab === "bookings" && <List data={bookings} empty="Aucune réservation pour tes artistes." testid="commerce-bookings" render={(b) => (
        <Row key={b.id} title={`${fmtDate(b.start)}`} sub={b.artist_name} status={b.status}
          right="" meta={b.order_id ? "Réservation confirmée" : ""} />
      )} />}

      {showQuote && <QuoteModal onClose={() => setShowQuote(false)} onCreated={() => { setShowQuote(false); setTab("quotes"); load(); }} />}
    </div>
  );
}

function QuoteModal({ onClose, onCreated }) {
  const [artists, setArtists] = useState([]);
  const [form, setForm] = useState({ artist_id: "", client_email: "", title: "", amount: "", message: "", delay: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/manager/commerce/artists").then((r) => setArtists(r.data.artists)).catch(() => setArtists([])); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.artist_id) { toast.error("Choisis l'artiste représenté"); return; }
    if (!form.client_email.trim()) { toast.error("Indique l'email du client"); return; }
    if (!form.title.trim() || !(Number(form.amount) > 0)) { toast.error("Titre et montant requis"); return; }
    setBusy(true);
    try {
      await api.post("/manager/commerce/quotes", { ...form, amount: Number(form.amount) });
      toast.success("Devis émis");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'émission du devis"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="quote-modal">
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 w-full max-w-lg space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl font-bold">Nouveau devis</h3>
          <button onClick={onClose} data-testid="quote-modal-close" className="text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button></div>
        <select value={form.artist_id} onChange={set("artist_id")} data-testid="quote-artist" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500">
          <option value="">Artiste représenté…</option>
          {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <input value={form.client_email} onChange={set("client_email")} placeholder="Email du client (compte Cehnura)" data-testid="quote-client" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
        <input value={form.title} onChange={set("title")} placeholder="Prestation (ex : Clip vidéo)" data-testid="quote-title" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
        <input value={form.amount} onChange={set("amount")} type="number" placeholder="Montant € (TTC)" data-testid="quote-amount" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
        <textarea value={form.message} onChange={set("message")} rows={2} placeholder="Conditions / description (optionnel)" data-testid="quote-message" className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 resize-none outline-none focus:border-amber-500" />
        <p className="text-xs text-zinc-500">Le paiement sera versé à l'artiste. Commission Cehnura appliquée automatiquement.</p>
        <button onClick={submit} disabled={busy} data-testid="quote-submit" className="w-full h-11 rounded-xl gold-gradient text-black font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Émettre le devis
        </button>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={testid}>
      <Icon className="w-4 h-4 text-amber-400 mb-2" />
      <div className="text-2xl font-display font-extrabold text-zinc-100">{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function List({ data, empty, render, testid }) {
  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  if (data.length === 0) return <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-10 text-center text-sm text-zinc-500" data-testid={`${testid}-empty`}>{empty}</div>;
  return <div className="space-y-2" data-testid={testid}>{data.map(render)}</div>;
}

function Row({ title, sub, status, right, meta, extra }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><span className="font-semibold text-zinc-100 truncate">{title || "—"}</span><StatusBadge status={status} /></div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate">{sub}</div>
        {(meta || extra) && <div className="text-xs text-zinc-600 mt-1">{meta}{extra ? ` · ${extra}` : ""}</div>}
      </div>
      {right && <span className="font-mono text-amber-300 shrink-0">{right}</span>}
    </div>
  );
}
